import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';
import { toastError } from '@components/Toast';
import ModalContainer from '@components/modals/ModalContainer';

import useDocumentTitle from '@hooks/useDocumentTitle';

import OrderDetails from '@features/order/components/OrderDetails';
import CreateTna from '@features/order/components/TNA/create-tna';
import TnaRunning from '@features/order/components/TNA/tna-running';
import TopBar from '@features/order/components/TopBar';
import OrderDetailsTabs from '@features/order/components/OrderDetailsTabs';
import DesignOrderModal from '@features/order/components/orderInfo/DesignOrderModal';

import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { fetchOrderDetailsInfo, fetchOrderPlaceWorkflow } from '@store/orders/actions';
import { getOrderDetailsInfo, getOrderPlaceWorkflow } from '@store/reducers';

import ApiClient from '@lib/apiClient';

const MyOrderDetails = () => {
	const [projectDetails, setProjectDetails] = useState({});
	const [loading, setLoading] = useState(false);
	const [orderCancelInfo, setOrderCancelInfo] = useState({});
	const [isOrderDetails, setIsOrderDetails] = useState(false);
	const { id } = useParams();
	const [currentStep, setCurrentStep] = useState('');
	const [allSteps, setAllSteps] = useState([]);
	const [designIdToView, setDesignIdToView] = useState(null);
	const [currentStatus, setCurrentStatus] = useState(null);
	const [selectedRequestId, setSelectedRequestId] = useState(null);
	const [urlRouteFrom, setUrlRouteFrom] = useState(null);
	const dispatch = useDispatch();
	const [isPendingTab, setIsPendingTab] = useState(true);
	const [productRefNumber, setProductRefNumber] = useState('');
	let orderPlaceMentStatus = useSelector(getOrderPlaceWorkflow);
	const [openOrderDesignModal, setOpenOrderDesignModal] = useState(false);
	const { isDrafted } = projectDetails;
	const location = useLocation();
	let orderDetailsInfo = useSelector(getOrderDetailsInfo);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const requestId = params.get('requestId');
		if (requestId) {
			getOrderCancelInfo(requestId);
		}
	}, []);

	const getOrderCancelInfo = (requestId) => {
		setLoading(true);
		ApiClient.GET('orderCancelRequest', `/${requestId}`)
			.then(({ data }) => {
				setOrderCancelInfo(data);
				setLoading(false);
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const onClickDesignImage = (designId) => {
		setDesignIdToView(designId);
		setTimeout(() => {
			let bodyElements = document.getElementsByTagName('body');
			if (bodyElements && bodyElements.length > 0) {
				bodyElements[0].setAttribute('class', 'modal-open');
			}
		}, 1000);
	};

	const onClose = () => {
		setOpenOrderDesignModal(false);
	};

	const onCloseViewModal = () => {
		setDesignIdToView(null);
		dispatch(clearDesignViewModalInfo({}));
	};

	const getOrderPlacementStatus = (id) => {
		dispatch(fetchOrderPlaceWorkflow(id)).then((response) => {
			setAllSteps(response);
		});
	};

	const onChangeStep = (step) => {
		setCurrentStep(step.stepName);
		if (step.stepName === 'TNA') {
			setIsPendingTab(false);
		} else {
			setIsPendingTab(true);
		}
		let urlSegment = `?workTab=${step.stepName}`;
		if (selectedRequestId) {
			urlSegment += `&requestId=${selectedRequestId}`;
		}
		if (urlRouteFrom) {
			urlSegment += `&routeFrom=${urlRouteFrom}`;
		}

		window.history.replaceState(null, null, urlSegment);
	};

	const onChangeOrderDetailsStep = (step) => {
		if (step === 'TNA') {
			setIsPendingTab(true);
			setIsOrderDetails(false);
		} else {
			if (projectDetails.status !== 'PENDING') {
				setIsOrderDetails(true);
			} else {
				setIsPendingTab(true);
			}
		}
		setCurrentStep(step);
	};

	const getProjectDetails = async (orderId) => {
		setLoading(true);
		dispatch(fetchOrderDetailsInfo(orderId)).then(() => {
			setLoading(false);
		});
	};

	const callback = useCallback(() => {
		getProjectDetails(id);
	}, [id]);

	const getTaskPayload = (data) => {
		if (data) {
			setCurrentStatus(data.orderStatus);
		}
	};

	useEffect(() => {
		setProjectDetails(orderDetailsInfo);
		setCurrentStatus(orderDetailsInfo.status);
	}, [orderDetailsInfo]);

	useEffect(() => {
		const search = location.search;
		const params = new URLSearchParams(search);
		const selectedTab = params.get('workTab');
		const selectedRoute = params.get('routeFrom');
		const requestId = params.get('requestId');

		if (requestId) {
			setUrlRouteFrom(selectedRoute);
			setSelectedRequestId(requestId);
		}
		if (selectedRoute === 'ORDER_CANCEL') {
			setIsOrderDetails(true);
			setCurrentStep('Order info');
		} else if (selectedTab === 'Commercial') {
			setIsOrderDetails(true);
			onChangeOrderDetailsStep('Commercial');
		} else if (selectedTab === 'Supplier') {
			setIsOrderDetails(true);
			onChangeOrderDetailsStep('Supplier');
		} else if (selectedTab === 'PI') {
			setIsOrderDetails(true);
			onChangeOrderDetailsStep('PI');
		} else if (selectedTab === 'Sales contract') {
			setIsOrderDetails(true);
			onChangeOrderDetailsStep('Sales contract');
		} else if (selectedTab === 'Order info') {
			setIsOrderDetails(true);
			onChangeOrderDetailsStep('Order info');
		} else if (selectedTab === 'Materials') {
			setIsOrderDetails(true);
			onChangeOrderDetailsStep('Materials');
		} else {
			if (orderDetailsInfo?.status === 'RUNNING') {
				if (selectedRoute === 'SCPI') {
					setIsOrderDetails(true);
				} else {
					setCurrentStep('TNA');
					setIsOrderDetails(false);
				}
			} else {
				if (!selectedRoute) {
					const startTab = allSteps.find((workflow) => workflow.status === 'PENDING');
					setCurrentStep(startTab?.stepName);
					if (startTab?.stepName !== 'TNA') {
						setIsOrderDetails(true);
					}
				} else {
					setCurrentStep(selectedTab);
				}
			}
		}
	}, [allSteps, orderDetailsInfo?.status]);

	useEffect(() => {
		getOrderPlacementStatus(id);
		getProjectDetails(id);
	}, [id]);

	useDocumentTitle(
		`Order profile ${
			projectDetails.name ? projectDetails.name : ''
		} Nitex - The easiest clothing manufacturing software`
	);

	useEffect(() => {
		if (designIdToView !== null) {
			openDesignViewModal();
		}
	}, [designIdToView]);

	const openDesignViewModal = () => {
		dispatch(
			setDesignViewModalInfo({
				id: designIdToView,
				onCloseViewModal: onCloseViewModal,
				source: 'ORDER_DETAILS',
				orderId: id
			})
		);
	};
	const renderTabButtons = () => {
		return (
			<div className="tna-navigation">
				<div className="tna-list d-flex">
					<OrderDetailsTabs
						currentStep={currentStep}
						orderCancelInfo={orderCancelInfo}
						orderPlaceMentStatus={orderPlaceMentStatus}
						isDrafted={isDrafted}
						type={projectDetails.status}
						onChangeStep={
							projectDetails.status === 'PENDING'
								? onChangeStep
								: (step) => onChangeOrderDetailsStep(step.stepName)
						}
					/>
				</div>
			</div>
		);
	};

	return (
		<LoaderComponent loading={loading}>
			<div className="wraper container-fluid order-admin-profile">
				<div className="top-header">
					<TopBar
						details={projectDetails}
						orderId={orderDetailsInfo?.id}
						onProjectDetailsCallBack={callback}
						styleLength={orderDetailsInfo?.orderTNAResponseList?.length}
						projectStatus={currentStatus}
						isDrafted={projectDetails?.isDrafted || false}
						setProductRefNumber={setProductRefNumber}
					/>

					{!isDrafted && renderTabButtons()}
				</div>

				{projectDetails.status !== undefined &&
					projectDetails.status !== 'PENDING' &&
					!loading &&
					(isOrderDetails ? (
						<OrderDetails
							orderId={id}
							currentStep={currentStep}
							onChangeStep={onChangeOrderDetailsStep}
							type={projectDetails.status}
							isDrafted={projectDetails?.isDrafted}
							poItemIdentifier={projectDetails?.poItemIdentifier}
							programTypeList={projectDetails?.programTypeList}
							productRefNumber={productRefNumber}
							setProductRefNumber={setProductRefNumber}
							getOrderCancelInfo={getOrderCancelInfo}
							orderCancelInfo={orderCancelInfo}
						/>
					) : orderDetailsInfo?.orderTNAResponseList?.length !== 0 ? (
						<div>
							<TnaRunning
								getTaskPayload={getTaskPayload}
								orderId={id}
								setDesignIdToView={onClickDesignImage}
								brandName={orderDetailsInfo?.brandResponse?.name}
							/>
						</div>
					) : (
						<div className="d-flex align-items-center justify-content-center py-5">
							<span className="regular-14"> The TNA has not been created yet</span>
						</div>
					))}

				{projectDetails.status !== undefined &&
					projectDetails.status === 'PENDING' &&
					(isPendingTab ? (
						<OrderDetails
							orderId={id}
							currentStep={currentStep}
							type="PENDING"
							onChangeStep={onChangeStep}
							isDrafted={projectDetails?.isDrafted}
							poItemIdentifier={projectDetails?.poItemIdentifier}
							programTypeList={projectDetails?.programTypeList}
							productRefNumber={productRefNumber}
							setProductRefNumber={setProductRefNumber}
							getOrderCancelInfo={getOrderCancelInfo}
							orderCancelInfo={orderCancelInfo}
						/>
					) : (
						<CreateTna orderId={id} />
					))}

				{openOrderDesignModal && (
					<ModalContainer onCloseModal={onClose} className="upload-style-modal">
						<DesignOrderModal
							orderId={id}
							onCloseModal={onClose}
							setProductRefNumber={setProductRefNumber}
						/>
					</ModalContainer>
				)}
			</div>
		</LoaderComponent>
	);
};

export default MyOrderDetails;
